/**
 * Claude Vision OCR Helper
 * Uses Anthropic's Claude 3.5 Sonnet to extract box score data from screenshots
 */

import { splitPlayerName, splitDbName, isNearLastName, isTotalsRow } from './nameNormalize';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Convert image file to base64 data URL, resizing large images for OCR
 * Keeps max 1920px wide at JPEG 85% quality — plenty for text recognition
 */
const imageToBase64 = (imageFile) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(imageFile);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      // Keep at 1920px — OCR was tuned at this resolution, don't risk accuracy
      const MAX_WIDTH = 1920;
      let { width, height } = img;
      if (width > MAX_WIDTH) {
        height = Math.round((height * MAX_WIDTH) / width);
        width = MAX_WIDTH;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      // Fallback: send as-is if canvas resize fails
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    };

    img.src = objectUrl;
  });
};

/**
 * Check if Claude Vision is available/configured
 */
export const isClaudeVisionAvailable = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/ocr/claude/status`);
    const data = await response.json();
    return data.available && data.configured;
  } catch (error) {
    console.error('❌ Failed to check Claude Vision status:', error);
    return false;
  }
};

/**
 * Extract box score data using Claude Vision
 * @param {File} imageFile - The screenshot image
 * @param {Array} players - List of players from database for matching
 * @param {Function} onProgress - Optional progress callback
 * @param {Array} teamPlayers - Optional list of players on the specific team (home/away) for priority matching
 * @returns {Promise<Object>} - Extracted box score data
 */
export const extractWithClaudeVision = async (imageFile, players = [], onProgress = null, teamPlayers = []) => {
  try {
    if (onProgress) onProgress(0.1);

    const base64Image = await imageToBase64(imageFile);

    if (onProgress) onProgress(0.3);

    const response = await fetch(`${API_BASE_URL}/ocr/claude`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        image: base64Image
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Claude Vision request failed');
    }

    const result = await response.json();

    if (onProgress) onProgress(0.9);


    // Match players from database - prioritize team players.
    // Die Summenzeile unter der Tabelle ("Ges." / "Total") sieht fuer die OCR
    // aus wie eine Spielerzeile — sie gehoert nicht in den Boxscore und wuerde
    // sonst als nicht zugeordnet im Hinweiskasten auftauchen.
    const matchedPlayers = result.data.players
      .filter(player => !isTotalsRow(player.name))
      .map(player => {
      const matched = matchPlayerByName(player.name, players, teamPlayers);
      return {
        originalName: player.name,
        playerName: matched ? `${matched.firstName} ${matched.lastName}` : player.name,
        playerId: matched?.id || null,
        matchConfidence: matched?.confidence || 'low',
        stats: {
          minutes: player.minutes,
          points: player.points,
          rebounds: player.rebounds,
          assists: player.assists,
          steals: player.steals,
          blocks: player.blocks,
          turnovers: player.turnovers,
          fieldGoalsMade: player.fieldGoalsMade,
          fieldGoalsAttempted: player.fieldGoalsAttempted,
          threePointersMade: player.threePointersMade,
          threePointersAttempted: player.threePointersAttempted,
          freeThrowsMade: player.freeThrowsMade,
          freeThrowsAttempted: player.freeThrowsAttempted,
          offensiveRebounds: player.offensiveRebounds,
          fouls: player.fouls
        }
      };
    });

    if (onProgress) onProgress(1.0);

    return {
      teamName: result.data.teamName,
      players: matchedPlayers,
      errors: [],
      metadata: {
        tokensUsed: result.tokensUsed,
        estimatedCost: result.estimatedCost
      }
    };

  } catch (error) {
    console.error('Claude Vision OCR Error:', error);
    throw new Error(`Claude Vision failed: ${error.message}`);
  }
};

/**
 * Match OCR player name to database player
 * Prioritizes players on the specific team (teamPlayers) over all players
 *
 * Verglichen wird in Teilen: Vorname, Nachname, Zusatz. Ein Name wie
 * "B. Portis Jr." wurde vorher als drei Woerter gelesen, der Rueckfall auf den
 * Nachnamen griff dann nach "jr" statt nach "portis" — und die Zeile fiel
 * lautlos aus dem Boxscore (Spiel 2070, Heat 54 statt 78 Punkte).
 */
const matchPlayerByName = (ocrName, players, teamPlayers = []) => {
  if (!players || players.length === 0) return null;

  const ocr = splitPlayerName(ocrName);
  if (!ocr.last) return null;

  // Helper: search a player list with all matching strategies
  const findInList = (list) => {
    const entries = list.map(player => ({ player, parts: splitDbName(player) }));

    // Der Zusatz unterscheidet, schliesst aber nie aus: 2K und Datenbank
    // fuehren ihn nicht immer gleich ("Smith Jr." vs. "Smith").
    const narrowBySuffix = (candidates) => {
      if (!ocr.suffix || candidates.length < 2) return candidates;
      const exact = candidates.filter(c => c.parts.suffix === ocr.suffix);
      return exact.length > 0 ? exact : candidates;
    };

    // Ausgeschriebener Vorname + Nachname
    if (ocr.first.length > 1) {
      const full = narrowBySuffix(entries.filter(e =>
        e.parts.first === ocr.first && e.parts.last === ocr.last
      ));
      if (full.length > 0) {
        return { ...full[0].player, confidence: 'high' };
      }
    }

    // Initial + Nachname (e.g., "J. Brunson" -> "Jalen Brunson")
    if (ocr.first) {
      const byInitial = narrowBySuffix(entries.filter(e =>
        e.parts.first.charAt(0) === ocr.first.charAt(0) && e.parts.last === ocr.last
      ));
      if (byInitial.length === 1) {
        return { ...byInitial[0].player, confidence: 'high' };
      } else if (byInitial.length > 1) {
        return { ...byInitial[0].player, confidence: 'medium' };
      }
    }

    // Nur der Nachname
    const byLast = narrowBySuffix(entries.filter(e => e.parts.last === ocr.last));
    if (byLast.length === 1) {
      return { ...byLast[0].player, confidence: 'medium' };
    } else if (byLast.length > 1) {
      return { ...byLast[0].player, confidence: 'low' };
    }

    // Letzte Chance: ein einzelner verlesener Buchstabe im Nachnamen. Treffer
    // hier sind bewusst 'low' — sie landen damit im Hinweiskasten und werden
    // vom Erfasser geprueft, statt lautlos aus der Tabelle zu fallen.
    const near = narrowBySuffix(entries.filter(e => isNearLastName(e.parts.last, ocr.last)));
    if (near.length === 1) {
      return { ...near[0].player, confidence: 'low' };
    }

    return null;
  };

  // First try matching within team players (higher priority)
  if (teamPlayers && teamPlayers.length > 0) {
    const teamMatch = findInList(teamPlayers);
    if (teamMatch) {
      return teamMatch;
    }
  }

  // Fallback to all players
  return findInList(players);
};

export default {
  extractWithClaudeVision,
  isClaudeVisionAvailable
};
